interface Font {
  fontFamily?: string;
  url?: string;
}

const isFontFile = (url: string) => {
  try {
    return /\.(woff2?|otf|ttf|eot)$/.test(new URL(url).pathname.toLowerCase());
  } catch {
    return false;
  }
};

const isLinked = (url: string) =>
  [...document.head.querySelectorAll('link[rel="stylesheet"]')].some(
    (link) => link.getAttribute('href') === url
  );

const appendStylesheet = (url: string) => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
  return new Promise<{ link: HTMLLinkElement; loaded: boolean }>((resolve) => {
    link.onload = () => resolve({ link, loaded: true });
    link.onerror = () => resolve({ link, loaded: false });
  });
};

const unquote = (value: string) => value.trim().replace(/^["']|["']$/g, '');

// Extract font familes from a stylesheet link
export const getFontFamilies = (stylesheetLink: HTMLLinkElement) => {
  let rules: CSSRule[];

  try {
    if (!stylesheetLink.sheet) {
      return undefined;
    }
    rules = [...stylesheetLink.sheet.cssRules];
  } catch {
    return undefined;
  }

  if (rules.some((rule) => rule instanceof CSSImportRule)) {
    return undefined;
  }

  return rules
    .filter((rule): rule is CSSFontFaceRule => rule instanceof CSSFontFaceRule)
    .map((rule) => unquote(rule.style.getPropertyValue('font-family')));
};

/**
 * Add a font to the document, either via a stylesheet link (for CSS fonts) or directly (for font files)
 * @see https://developer.mozilla.org/en-US/docs/Web/API/CSS_Font_Loading_API
 */
export const loadFont = async (font?: Font) => {
  if (!(font?.fontFamily && font?.url)) {
    return;
  }

  // Font service URLs (Google Fonts, Adobe Fonts) serve CSS, not a font file,
  // so they have to go through a <link> rather than the FontFace API.
  if (!isFontFile(font.url)) {
    // If the stylesheet is already linked, don't link it again
    if (!isLinked(font.url)) {
      void appendStylesheet(font.url);
    }
    return;
  }

  if (typeof FontFace === 'undefined') {
    throw Error('FontFace is undefined');
  }

  const externalFont = new FontFace(font.fontFamily, `url(${font.url})`);
  document.fonts.add(externalFont);
  return await externalFont.load();
};

/**
 * Check whether a font can be loaded from this URL under the family it was given
 */
export const validateFont = async (font?: Font) => {
  const { fontFamily, url } = font || {};

  if (!(fontFamily && url)) {
    return false;
  }

  if (isFontFile(url)) {
    // Guard for non-browser runtimes like jsdom + SSR
    if (typeof FontFace === 'undefined') {
      return false;
    }
    try {
      await new FontFace(fontFamily, `url(${url})`).load();
      return true;
    } catch {
      return false;
    }
  }

  const { link, loaded } = await appendStylesheet(url);
  const families = loaded ? getFontFamilies(link) : undefined;
  link.remove();

  if (!loaded) {
    return false;
  }

  // Undefined families means the rules were unreadable, so allow the font
  return (
    !families ||
    families.some((family) => family.toLowerCase() === fontFamily.toLowerCase())
  );
};
